/*
PWA entry point.
Boot order: open IndexedDB, load species index, decide cold-start route
based on onboardingDone, mount router, register service worker.

The SW is only registered in production builds — Vite's dev server would
otherwise serve the SW with a wrong MIME / break HMR.
*/

import { startRouter, kPaths } from './router'
import { openDb } from './data/db'
import { ensureSpeciesIndex } from './data/speciesIndex'
import { isOnboardingDone } from './data/onboardingStore'
import { installSwUpdateBanner } from './ui/components/SwUpdateBanner'
import { installInstallPrompt } from './ui/components/InstallPrompt'
import { bootstrapGeolocation } from './capture/geolocation'
import { maybeInstallDebugOverlay } from './ui/components/DebugOverlay'
import './app.css'

const kSwUrl: string = '/sw.js'  // service worker served from public root

async function boot(): Promise<void>
{
	// Optional on-device debug overlay (smoke-test tooling). No-op
	// unless window.location.hash contains 'debug'. Mounted before
	// startRouter so it captures every console line from boot onwards.
	maybeInstallDebugOverlay()

	const vRoot = document.getElementById('app') as HTMLElement | null  // mount node from index.html
	if (!vRoot)
	{
		console.error('Missing #app root in index.html')
		return
	}

	let vInitialHash: string | null = null  // forced startDestination, null when the URL hash drives the route
	let vOnboardingDone: boolean = false  // gates the GPS warm-up so we don't fire the prompt before WelcomeScreen
	try
	{
		await openDb()
		await ensureSpeciesIndex()
		vOnboardingDone = await isOnboardingDone()
		if (!vOnboardingDone && !window.location.hash) vInitialHash = kPaths.welcome()
	}
	catch (vErr)
	{
		console.error('Boot failed during data init', vErr)
	}

	startRouter(vRoot, vInitialHash)
	// Fire-and-forget GPS warm-up: starts the permanent watchPosition
	// so the user-location dot lights up as soon as a fix lands and
	// the capture flow always has a fresh fix to short-circuit on.
	// Parity with Android's LocationProvider eager-init.
	//
	// Gated on onboardingDone so the OS prompt only appears AFTER the
	// user has read the privacy note on WelcomeScreen — WelcomeScreen
	// itself fires bootstrapGeolocation() at "Commencer". The `debug`
	// hash bypass lets smoke-test sessions skip the WelcomeScreen
	// without losing the blue dot (explicit opt-in via /#debug).
	const vHashLower = window.location.hash.toLowerCase()
	if (vOnboardingDone || vHashLower.includes('debug')) bootstrapGeolocation()
	installSwUpdateBanner()  // listen for SW activations triggered by registerServiceWorker below
	installInstallPrompt()   // capture beforeinstallprompt + surface the iOS Add-to-Home-Screen hint
	registerServiceWorker()
}

/*
Register the service worker. iOS Safari requires HTTPS (or localhost) and
the file to be served from the registered scope.
*/
function registerServiceWorker(): void
{
	if (!('serviceWorker' in navigator)) return
	if (!import.meta.env.PROD) return
	navigator.serviceWorker
		.register(kSwUrl, { scope: '/' })
		.catch((inErr: unknown) => { console.warn('SW registration failed', inErr) })
}

void boot()
